import { Controller } from "@hotwired/stimulus"
import { isServerErrorStatus } from "../lib/global_error_modal"
import { buildGlAlert } from "../lib/gl_alert"

// NIPR Data modal (#14708): the lazy `nipr_data_frame` must never sit on
// "Loading NIPR data…" forever. A server error (5xx / 408) or a request that
// never got a response swaps the spinner for an inline, non-technical error
// inside the modal, and the next time the modal opens the frame is fetched
// again. Every other frame-missing response (a redirect to sign-in, a refused
// viewer) still bubbles to the global turbo:frame-missing handler, and a
// network failure still reaches the global session probe — this controller
// only owns what the user sees inside the modal. The alert is built here from
// the error-message value, so no alert markup exists in the page before a
// failure (a server-side <template> reads as live DOM to an HTML4 parser).
export default class extends Controller {
  static targets = ["frame"]
  static values = { errorMessage: String }

  connect() {
    this.failed = false
    this.loadingMarkup = this.frameTarget.innerHTML
    this.modal = this.element.closest(".modal")
    this.modal?.addEventListener("show.bs.modal", this.retryAfterFailure)
  }

  disconnect() {
    this.modal?.removeEventListener("show.bs.modal", this.retryAfterFailure)
  }

  frameMissing(event) {
    const response = event.detail?.response
    if (!response || !isServerErrorStatus(response.status)) return

    // Ours to show inline — keep the global handler from stacking its modal
    // over this one.
    event.preventDefault()
    event.stopPropagation()
    this.showError()
  }

  fetchFailed(event) {
    if (event.detail?.error?.name === "AbortError") return
    this.showError()
  }

  showError() {
    this.failed = true
    const alert = buildGlAlert({
      tone: "danger",
      layout: "row",
      role: "alert",
      attributes: { "data-testid": "nipr-data-error" },
      body: [this.errorMessageValue],
    })
    this.frameTarget.replaceChildren(alert)
  }

  retryAfterFailure = () => {
    if (!this.failed) return

    this.failed = false
    this.frameTarget.innerHTML = this.loadingMarkup
    this.frameTarget.reload()
  }
}
